import { useRef, useState } from "react"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { Link } from "react-router"
import { ArrowUpRight, ChevronDown, MessageChatCircle, Phone01, User01 } from "@untitledui/icons"

import { BookPane, type BookMode } from "./book-pane"
import { CustomerPane } from "./customer-pane"
import { Decisions, FIELD } from "./decisions"
import { EstimatePane } from "./estimate-pane"
import { ThreadSkeleton } from "./skeletons"
import { TaskChip } from "./task-chip"
import { CustomerConversation } from "@/components/customers/customer-conversation"
import { STATUS_CFG } from "@/components/estimates/status-badge"
import type { ApiEstimate, EstimateStatus } from "@/lib/api/types"
import type { CustomerActivityResponse } from "@/lib/api/customer-activity"
import {
  DECLINE_LABEL,
  OUTCOME_LABEL,
  attemptLabel,
  type AnswerTaskResponse,
  type EstimateFollowUpHead,
  type FollowUpCadence,
  type TaskAnswer,
  type TaskDoc,
  type TaskRow,
} from "@/lib/api/tasks"
import {
  STAGES,
  TASK_KIND,
  facts,
  kindOf,
  stageAfter,
  stageIndex,
  stageLabel,
  type TaskResult,
} from "@/lib/tasks/task-types"
import { withRestart, type DecisionCtx, type TaskApi, type VisitMode } from "@/lib/tasks/decisions"
import { dueLine, whenLabel } from "@/lib/tasks/task-copy"
import { formatPhone } from "@/lib/utils/phone"
import { cx } from "@/lib/utils/cx"

// The everyday desk, laid out the way queue tools are: the WORK in the
// middle — who and where they are, do it, log it, the history — and a
// CONTEXT RAIL beside it that never moves: the calendar, the messages, the
// estimate, always in the same place on every task. Nothing hides under a
// tab. Dark = do, green = book, white = log, red = close.

/** Everything the frame does to the world. Built once per row by the workspace. */
export interface TaskIO extends TaskApi {
  busy: boolean
  post: (answer: TaskAnswer, note: string | null) => Promise<AnswerTaskResponse>
  /** Answer a task other than the row's — the fix task the engine just made. */
  postTo: (taskId: number, answer: TaskAnswer, note: string | null) => Promise<AnswerTaskResponse>
}

export interface TaskFrameProps {
  row: TaskRow
  teamId: number
  cadence: FollowUpCadence | null
  io: TaskIO
  data: {
    estimate: ApiEstimate | undefined
    activity: CustomerActivityResponse | undefined
    history: TaskDoc[] | undefined
    meId: number | null
  }
  onDone: (result: TaskResult) => void
  onSwitchToCall?: () => void
}

function money(n: number): string {
  return `$${Math.round(n).toLocaleString("en-US")}`
}

function pretty(s: string): string {
  return s.replace(/_/g, " ").toLowerCase()
}

const DO_BUTTON =
  "inline-flex items-center justify-center gap-2.5 h-12 px-5 rounded-lg bg-zinc-900 hover:bg-black text-white text-[15px] font-semibold tabular-nums transition-[background-color,transform] duration-150 ease-out-quart active:scale-[0.99] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/30 focus-visible:ring-offset-2 disabled:opacity-40 disabled:active:scale-100"

const EASE = [0.23, 1, 0.32, 1] as const

export function TaskFrame({ row, teamId, cadence, io, data, onDone, onSwitchToCall }: TaskFrameProps) {
  const kind = kindOf(row.type)
  const spec = TASK_KIND[kind]
  const persisted = row.id > 0
  const due = dueLine(row)
  const first = row.customerName.split(" ")[0] || row.customerName

  const [note, setNote] = useState("")
  const [phone, setPhone] = useState("")
  const [restart, setRestart] = useState(false)
  const [lit, setLit] = useState<EstimateStatus | null>(null)
  const [visitMode, setVisitMode] = useState<VisitMode>("ask")
  const composerRef = useRef<HTMLTextAreaElement>(null)

  const events = data.activity?.events ?? []
  const f = facts(row, data.meId)
  // The fix card is the missing number; saying "no number" again is noise.
  const fragments = [
    f.lastCall && { label: "Last call", text: f.lastCall, off: false },
    f.wroteBack && { label: null, text: f.wroteBack, off: false },
    kind !== "fix" && f.texts && { label: "Texts", text: f.texts.text, off: f.texts.off },
  ].filter((x): x is { label: string | null; text: string; off: boolean } => !!x)
  // Messages are a reply to something the customer wrote. Until they have,
  // there is nothing to answer.
  const hasInbound = row.lastMessage?.direction === "inbound" || events.some((e) => e.kind === "replied" && !!e.body)

  const ctx: DecisionCtx = { row, teamId, note: note.trim(), api: io, switchToCall: onSwitchToCall }

  // The strip lights up first; the card advances a beat later, so she sees
  // the effect of what she just said before the next task takes over.
  const finish = (r: TaskResult) => {
    setLit(stageAfter(row, r))
    window.setTimeout(() => onDone(r), 450)
  }
  const headOf = (res: AnswerTaskResponse) => (res.estimate.metadata?.followUp ?? undefined) as EstimateFollowUpHead | undefined
  const postAnswer = async (a: TaskAnswer, meta?: { bookedAt?: string; after?: () => Promise<void>; startedTexts?: boolean }) => {
    const answer = withRestart(a, restart)
    const res = await io.post(answer, ctx.note || null)
    if (meta?.after) await meta.after()
    finish({ kind: "answered", answer, next: res.next, head: headOf(res), bookedAt: meta?.bookedAt, startedTexts: meta?.startedTexts })
  }
  // Wrong number, with the new number in hand: log the dead one, save the
  // new one, close the fix task — three writes, one click — and the engine
  // hands back the call to make right now on the new number.
  const wrongNumber = async (newPhone: string | null) => {
    const res1 = await io.post({ kind: "bad_number" }, ctx.note || null)
    if (!newPhone || !res1.next || res1.next.type !== "fix_contact") {
      finish({ kind: "answered", answer: { kind: "bad_number" }, next: res1.next, head: headOf(res1) })
      return
    }
    await io.savePhone(row.estimateId, newPhone)
    const res2 = await io.postTo(res1.next.id, { kind: "contact_fixed" }, null)
    finish({ kind: "answered", answer: { kind: "contact_fixed" }, next: res2.next, head: headOf(res2) })
  }
  const fixNumber = async () => {
    await io.savePhone(row.estimateId, phone.trim())
    await postAnswer({ kind: "contact_fixed" })
  }

  // The calendar's job on this card.
  const bookMode: BookMode = visitMode === "rebook" ? "rebook" : "book"
  const beforeBook = kind === "visit" && visitMode === "rebook" ? () => io.setVisitOutcome(row.appointmentId!, "no_show") : undefined
  const onBooked = (appointmentId: number, startsAt: string) => {
    if (kind === "visit" && visitMode === "rebook") {
      finish({ kind: "visit", outcome: "no_show", rebookedAt: startsAt })
    } else if (persisted) {
      void postAnswer({ kind: "reached", then: "booked", appointmentId }, { bookedAt: startsAt })
    } else {
      // A reply has no task to answer — the booking itself moved it.
      finish({ kind: "booked", at: startsAt })
    }
  }

  const lineItems = data.estimate?.lineItems ?? []
  const work = (() => {
    const parts = lineItems.slice(0, 2).map((li) => pretty(li.component))
    const more = lineItems.length > 2 ? ` +${lineItems.length - 2}` : ""
    return parts.length ? parts.join(", ") + more : null
  })()

  const conversation = (withComposer: boolean) =>
    row.customerId && data.activity ? (
      <CustomerConversation
        customerId={row.customerId}
        events={data.activity.events}
        consent={data.activity.consent}
        estimateId={row.estimateId}
        customerName={row.customerName}
        frameless
        {...(withComposer ? { composerRef, onSent: () => finish({ kind: "replied" }) } : {})}
      />
    ) : row.customerId ? (
      <ThreadSkeleton />
    ) : (
      <p className="text-[13px] text-zinc-500">No customer record, so no messages.</p>
    )

  return (
    <>
      {/* ── The work ── */}
      <article className="rounded-2xl border border-zinc-200/80 bg-white overflow-hidden animate-in fade-in duration-150 motion-reduce:animate-none">
        <header className="px-6 pt-5 pb-4 @container">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <TaskChip kind={kind} size="md" />
                {spec.subLabel(row, cadence) && <span className="text-[13px] font-medium text-zinc-600">{spec.subLabel(row, cadence)}</span>}
              </div>
              <h2 className="mt-2 text-[22px] font-bold text-zinc-900 tracking-tight leading-none">{row.customerName}</h2>
              <p className="mt-1.5 text-[14px] text-zinc-600">
                {row.vehicleLabel}
                {work && (
                  <>
                    <span className="text-zinc-300 mx-1.5">·</span>
                    <span className="capitalize">{work}</span>
                  </>
                )}
                <span className="text-zinc-300 mx-1.5">·</span>
                <span className="font-semibold text-zinc-900 tabular-nums">{money(row.avg)}</span>
              </p>
            </div>
            <p
              className={cx(
                "shrink-0 text-[13px] font-semibold mt-0.5 text-right",
                due.tone === "overdue" && "text-red-600",
                due.tone === "now" && "text-amber-700",
                due.tone === "quiet" && "text-zinc-500",
              )}
            >
              {due.text}
            </p>
          </div>
          {/* The facts — labelled fragments, one line, only what exists. */}
          {fragments.length > 0 && (
            <p className="mt-3 text-[13px] leading-snug">
              {fragments.map((fr, i) => (
                <span key={i}>
                  {i > 0 && <span className="mx-2 text-zinc-300">·</span>}
                  {fr.label && <span className="text-zinc-500">{fr.label}: </span>}
                  <span className={cx(fr.off ? "text-red-700 font-medium" : "text-zinc-700")}>{fr.text}</span>
                </span>
              ))}
            </p>
          )}
          <StageStrip status={lit ?? (row.estimateStatus as EstimateStatus)} preview={lit != null} />
        </header>

        <section className="border-t border-zinc-200/80 px-6 py-5 min-w-0">
          {kind === "call" &&
            (row.phone ? (
              <a href={`tel:${row.phone.replace(/[^\d+]/g, "")}`} className={DO_BUTTON}>
                <Phone01 className="size-[18px]" aria-hidden />
                Call {formatPhone(row.phone)}
              </a>
            ) : (
              <p className="text-[14px] font-medium text-amber-700">No number on file. Use "Wrong number" below to add one.</p>
            ))}
          {kind === "reply" && conversation(true)}
          {/* A review IS looking at the estimate: photos, every line, the range, the flag. */}
          {kind === "review" && <EstimatePane estimate={data.estimate} row={row} full />}
          {kind === "fix" && (
            <div>
              <label className="block text-[13px] font-medium text-zinc-600 mb-1.5" htmlFor="new-number">
                New number for {first}
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  id="new-number"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  aria-label={`${first}'s new phone number`}
                  className={cx(FIELD, "h-12 w-56 text-[16px]")}
                />
                <button type="button" disabled={io.busy || phone.replace(/\D/g, "").length < 10} onClick={() => void fixNumber()} className={DO_BUTTON}>
                  Save and call
                </button>
              </div>
            </div>
          )}
          <div className={cx(kind === "reply" ? "mt-5" : kind === "visit" || kind === "wrapup" ? "" : "mt-6")}>
            <Decisions
              kind={kind}
              row={row}
              mode={visitMode}
              ctx={ctx}
              busy={io.busy}
              note={note}
              onNote={setNote}
              restart={restart}
              onRestart={setRestart}
              onPost={(a, opts) => void postAnswer(a, opts)}
              onDone={finish}
              onMode={setVisitMode}
              onWrongNumber={(p) => void wrongNumber(p)}
            />
          </div>
        </section>

        <HistorySection tasks={data.history ?? []} meId={data.meId} />
      </article>

      {/* ── The context rail ── always the same sections, always in the same place. */}
      <aside
        className="mt-4 xl:mt-0 rounded-2xl border border-zinc-200/80 bg-white divide-y divide-zinc-200/80 xl:sticky xl:top-0 xl:max-h-[calc(100dvh-8.75rem)] xl:overflow-y-auto overscroll-contain min-w-0"
        aria-label="Calendar, messages and estimate"
      >
        {spec.rail.map((section) => {
          if (section === "messages" && !hasInbound) return null
          if (section === "customer") {
            return (
              <div key={section} className="px-5 py-5">
                <CustomerPane estimate={data.estimate} row={row} />
              </div>
            )
          }
          if (section === "calendar") {
            return (
              <div key={section} className="px-5 py-5">
                <BookPane
                  row={row}
                  teamId={teamId}
                  mode={bookMode}
                  waiting={kind === "visit" && visitMode === "ask"}
                  beforeBook={beforeBook}
                  onBooked={onBooked}
                  defaultOpen={kind === "visit"}
                />
              </div>
            )
          }
          if (section === "messages") {
            return (
              <div key={section} className="px-5 py-5">
                <div className="flex items-center gap-2 mb-3">
                  <MessageChatCircle className="size-4 text-zinc-500" aria-hidden />
                  <h3 className="text-[15px] font-semibold text-zinc-900">Messages</h3>
                </div>
                {conversation(false)}
              </div>
            )
          }
          return (
            <div key={section} className="px-5 py-5">
              <EstimatePane estimate={data.estimate} row={row} />
            </div>
          )
        })}
        {row.customerId && (
          <div className="px-5 py-2.5 -ml-1">
            <Link to={`/customers/${row.customerId}`} className={RAIL_LINK}>
              <User01 className="size-3.5" aria-hidden /> {first}'s record <ArrowUpRight className="size-3" aria-hidden />
            </Link>
          </div>
        )}
      </aside>
    </>
  )
}

const RAIL_LINK =
  "inline-flex items-center gap-1 h-9 px-2 rounded-md text-[13px] font-medium text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 transition-colors duration-150 ease-out-quart motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900/20"

/** The pipeline's stages, in its own words and colours. */
function StageStrip({ status, preview }: { status: EstimateStatus; preview: boolean }) {
  if (status === "Declined") {
    return (
      <p className="mt-4 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-red-600">
        <span className="size-2.5 rounded-full bg-red-400" aria-hidden /> Declined
      </p>
    )
  }
  const at = stageIndex(status)
  return (
    <ol className="mt-4 flex items-center">
      {STAGES.map((s, i) => {
        const reached = i <= at
        const current = i === at
        return (
          <li key={s} className={cx("flex items-center min-w-0", current || i === STAGES.length - 1 ? "flex-none" : "flex-1")}>
            <div className="flex items-center gap-1.5 shrink-0">
              <span
                className={cx(
                  "size-2.5 rounded-full transition-[background-color,box-shadow] duration-200 ease-out-quart motion-reduce:transition-none",
                  current ? cx(STATUS_CFG[s].dot, preview ? "ring-4 ring-emerald-100" : "ring-4 ring-zinc-100") : reached ? "bg-zinc-900" : "bg-zinc-200",
                )}
                aria-hidden
              />
              {/* In a narrow column only the current stop gets its word; the rest are dots. */}
              <span
                className={cx(
                  "text-[12.5px] leading-none whitespace-nowrap transition-colors duration-200 ease-out-quart motion-reduce:transition-none",
                  !current && "hidden @xl:inline",
                  current ? cx("font-semibold", preview ? "text-emerald-700" : "text-zinc-900") : reached ? "font-medium text-zinc-600" : "text-zinc-500",
                )}
              >
                {stageLabel(s)}
              </span>
            </div>
            {i < STAGES.length - 1 && (
              <span className={cx("mx-2.5 h-px flex-1 min-w-3 transition-colors duration-200 ease-out-quart motion-reduce:transition-none", i < at ? "bg-zinc-900" : "bg-zinc-200")} aria-hidden />
            )}
          </li>
        )
      })}
    </ol>
  )
}

/** What's been logged on this estimate — a foot of the card, folded until asked. */
function HistorySection({ tasks, meId }: { tasks: TaskDoc[]; meId: number | null }) {
  const [open, setOpen] = useState(false)
  const reduce = useReducedMotion()
  const answered = tasks.filter((t) => t.status === "done" && t.answer)
  const describe = (t: TaskDoc): string => {
    const who = t.answeredBy?.id === meId ? "You" : (t.answeredBy?.name ?? "Someone")
    const a = t.answer!
    const tries = t.attempt > 1 ? ` · ${attemptLabel(t.attempt)}` : ""
    switch (a.kind) {
      case "confirmed":
        return `${who} confirmed the estimate`
      case "reached": {
        const then =
          a.then === "booked" ? " — booked a visit"
            : a.then === "callback" ? " — call back requested"
              : a.then === "declined" ? ` — passed (${DECLINE_LABEL[a.declineReason ?? "other"].toLowerCase()})`
                : a.then === "sent_estimate" ? " — sent the estimate"
                  : a.then === "needs_changes" ? " — wants changes"
                    : " — still thinking"
        return `${who} called — ${OUTCOME_LABEL.reached}${then}${tries}`
      }
      case "no_answer":
      case "voicemail":
      case "bad_number":
        return `${who} called — ${OUTCOME_LABEL[a.kind]}${tries}`
      case "contact_fixed":
        return `${who} fixed the number`
      case "closed":
        return `${who} closed it — ${DECLINE_LABEL[a.declineReason ?? "unreachable"].toLowerCase()}`
      case "keep_trying":
        return a.callbackAt ? `${who} chose to call again ${whenLabel(a.callbackAt, new Date(), false)}` : `${who} chose to try once more`
      case "parked":
        return `${who} parked it`
    }
  }
  return (
    <div className="border-t border-zinc-200/80">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full h-12 px-6 flex items-center justify-between text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-900/20"
      >
        <span className="text-[14px] font-semibold text-zinc-900">
          History
          <span className="ml-1.5 text-[13px] font-medium text-zinc-500 tabular-nums">
            {answered.length === 0 ? "nothing logged yet" : `${answered.length} ${answered.length === 1 ? "entry" : "entries"}`}
          </span>
        </span>
        <ChevronDown className={cx("size-4 text-zinc-500 transition-transform duration-200 ease-out-quart motion-reduce:transition-none", open && "rotate-180")} aria-hidden />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
            exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
            transition={{ duration: reduce ? 0.12 : 0.2, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-5">
              {answered.length === 0 ? (
                <p className="text-[13.5px] text-zinc-500">Nothing answered yet.</p>
              ) : (
                <ol className="space-y-3">
                  {answered.map((t) => (
                    <li key={t.id} className="flex items-start gap-3">
                      <span className="mt-[7px] size-1.5 rounded-full bg-zinc-900 shrink-0" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="text-[14px] text-zinc-900 leading-snug">{describe(t)}</p>
                        {t.note && <p className="text-[13px] text-zinc-600 italic mt-0.5">"{t.note}"</p>}
                        <p className="text-[12.5px] text-zinc-500 mt-0.5 tabular-nums">
                          {new Date(t.answeredAt!).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" })}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
